import styled, { css } from 'styled-components';
import { rem } from 'polished';

const shimmer = css`
  @keyframes poDetailSkelShimmer {
    0% {
      background-position: 300% 0;
    }
    100% {
      background-position: -300% 0;
    }
  }
`;

export const Skel = styled.div`
  ${shimmer}
  height: ${props => props.$height || rem('12px')};
  width: ${props => props.$width || '100%'};
  max-width: ${props => props.$maxWidth || '100%'};
  border-radius: ${props => props.$radius || rem('4px')};
  flex-shrink: ${props => (props.$shrink === false ? 1 : 0)};
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.1) 40%, rgba(0, 0, 0, 0.04) 80%);

  body[data-theme='dark'] & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.14) 40%, rgba(255, 255, 255, 0.04) 80%);
  }
  background-size: 300% 100%;
  animation: poDetailSkelShimmer 1.8s ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
`;

export const PageSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 1199px) {
    overflow: visible;
    height: auto;
  }
`;

export const ParamsSkeletonBox = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: ${rem('20px')};
  row-gap: ${rem('16px')};
  padding: ${rem('16px')} ${rem('20px')};
  border-radius: 10px;
  box-sizing: border-box;
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #082952;
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const ParamSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('8px')};
  min-width: 0;
`;

export const BodySkeleton = styled.div`
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
`;

export const ProductsSkeletonPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
`;

export const ProductsToolbarSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('12px')};
  padding: ${rem('8px')} ${rem('2px')} ${rem('12px')};
  flex: 0 0 auto;
`;

export const ProductsToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem('10px')};
  flex: 1 1 auto;
  min-width: 0;
  margin-left: auto;
`;

export const SearchSkel = styled(Skel)`
  flex: 0 1 ${rem('280px')};
  width: ${rem('280px')};
  max-width: ${rem('320px')};
  height: ${rem('36px')};
  border-radius: 8px;
`;

export const TableSkeletonShell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #022443;
  }
  border: none;
  
  body[data-theme='dark'] & {
    border: 1px solid #2F76C5;
  }
  border-radius: ${rem('4px')};
  
  body[data-theme='dark'] & {
    border-radius: 12px;
  }
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);

  body[data-theme='dark'] & {
    box-shadow: -2px 2px 4px #2f76c5;
  }
`;

export const TableHeadSkeleton = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) repeat(3, minmax(0, 0.7fr));
  align-items: center;
  gap: ${rem('16px')};
  height: 36px;
  min-height: 36px;
  padding: 0 ${rem('16px')};
  background: #eeeeee;
  
  body[data-theme='dark'] & {
    background: #263E59;
  }
`;

export const ProductRowSkeleton = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) repeat(3, minmax(0, 0.7fr));
  align-items: center;
  gap: ${rem('16px')};
  min-height: ${rem('72px')};
  padding: 0 ${rem('16px')};
  border-bottom: 1px solid ${props => props.theme.colors.S[400]};
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

export const ProductInfoSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('12px')};
  min-width: 0;
`;

export const ProductLinesSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('6px')};
  min-width: 0;
  flex: 1;
`;

export const TotalsSkeletonBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('4px')};
  flex: 0 0 auto;
  margin: ${rem('12px')} 0 0;
  padding: ${rem('12px')} ${rem('16px')};
  border-radius: 7px;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #002d5f;
  }
  border: 1px solid ${props => props.theme.colors.S[500]};

  body[data-theme='dark'] & {
    border-color: #e9cb7a;
  }
`;

export const TotalsRowSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem('12px')};

  ${props =>
    props.$total &&
    css`
      margin-top: ${rem('2px')};
      padding-top: ${rem('8px')};
      border-top: 1px solid ${props => props.theme.colors.S[500]};

      body[data-theme='dark'] & {
        border-top-color: rgba(233, 203, 122, 0.35);
      }
    `}
`;

export const AsideSkeletonBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #0B2033;
  }
  border-radius: 10px;
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

export const AsideTabsSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('16px')};
  height: 48px;
  min-height: 48px;
  padding: 0 ${rem('16px')};
  border-bottom: 1px solid ${props => props.theme.colors.S[400]};
  
  body[data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

export const AsideBodySkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('8px')};
  padding: ${rem('16px')} ${rem('12px')};
`;

export const TimelineItemSkeleton = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem('12px')};
  padding-bottom: ${rem('28px')};
`;

export const TimelineLinesSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem('8px')};
  flex: 1;
  min-width: 0;
  padding-top: 4px;
`;

export const CrumbSkeletonStrip = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem('10px')};
  height: ${rem('55px')};
  min-height: ${rem('55px')};
  padding: 0 ${rem('16px')};
  box-sizing: border-box;
  background: #ffffff;
  
  body[data-theme='dark'] & {
    background: #0B2033;
  }
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
`;

export const PageSkeletonShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #f2f3f5;
  
  body[data-theme='dark'] & {
    background: transparent;
  }
`;
